import type { ReactNode } from "react";
import { Link } from "wouter";
import { Logo } from "../ui/index.ts";

const UPDATED = "November 2, 2026";

type Section = { title: string; body: ReactNode };

const privacy: Section[] = [
  {
    title: "What we collect",
    body: (
      <ul>
        <li>Your account: your email, and your name and photo if you sign in with Google. Passwords are stored hashed by Supabase, never by us.</li>
        <li>Your profile: what you type in Settings, such as your name, phone, location, links, work authorization and standing answers.</li>
        <li>Your resume, in a private storage bucket only your account can read.</li>
        <li>What you do with jobs: saves, swipes, the jobs you apply to, and each application's answers, status and confirmation.</li>
      </ul>
    ),
  },
  {
    title: "How we use it",
    body: (
      <p>
        To rank jobs for you, write fit notes, and fill in and submit the applications you approve. Nothing is sent to an employer until you press Apply on
        that job. We don't sell your data and we don't show ads.
      </p>
    ),
  },
  {
    title: "Who sees it",
    body: (
      <ul>
        <li>Employers you apply to. Each one gets the profile fields, resume and answers its own application form asks for, through its applicant tracking system.</li>
        <li>Supabase stores your account, profile, resume and applications. Cloudflare hosts the site and API.</li>
        <li>Meta's Model API (Muse Wonder) reads your profile and a job's description to write fit notes and draft answers to open questions. It doesn't get your resume file.</li>
        <li>Google, if you choose to sign in with Google.</li>
      </ul>
    ),
  },
  {
    title: "Signing in with Google",
    body: (
      <p>
        If you sign in with Google, we receive your name, email address and profile photo from Google, and nothing else. We use them only to create and sign
        you in to your Headcount account and to fill in your name and email on your profile. They're stored with your account in Supabase and deleted with it.
        Your name and email reach an employer only inside an application you approve, like the rest of your profile. We don't sell them, use them for ads,
        or use them to train AI models. Headcount's
        use of information received from Google APIs adheres to the{" "}
        <a href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</a>, including the Limited Use
        requirements.
      </p>
    ),
  },
  {
    title: "Keeping and deleting it",
    body: (
      <p>
        We keep your data while your account is open. Delete your account and everything in it any time from Settings, under Account. A paid plan ends
        then, with no refund. If you can't sign in, email <a href="mailto:hello@itsheadcount.com">hello@itsheadcount.com</a> and we'll delete it for you.
        Applications already sent stay with the employers you sent them to.
      </p>
    ),
  },
  {
    title: "Contact",
    body: (
      <p>
        Questions about this policy or your data? Email <a href="mailto:hello@itsheadcount.com">hello@itsheadcount.com</a>.
      </p>
    ),
  },
];

const terms: Section[] = [
  {
    title: "Using Headcount",
    body: (
      <p>
        Headcount finds open roles on company career pages and applies to the ones you approve, using the profile and resume you give it. You need to be 16 or
        older and use your own name and real details.
      </p>
    ),
  },
  {
    title: "Your applications",
    body: (
      <p>
        You decide what gets sent. Check your profile and standing answers before you apply: they go to employers as written. You're responsible for
        what your applications say. We don't promise interviews or offers.
      </p>
    ),
  },
  {
    title: "Limits",
    body: (
      <p>
        Each account sends a capped number of applications a day. Don't use Headcount to send spam, apply under someone else's name, or get around an
        employer's rules. We can suspend accounts that do.
      </p>
    ),
  },
  {
    title: "The service",
    body: (
      <p>
        Job listings come from employers' public job boards and may be out of date or wrong. Some forms can't be completed automatically and will show as
        failed. Headcount is provided as is, and we may change or stop parts of it.
      </p>
    ),
  },
  {
    title: "Contact",
    body: (
      <p>
        Questions about these terms? Email <a href="mailto:hello@itsheadcount.com">hello@itsheadcount.com</a>.
      </p>
    ),
  },
];

/** The privacy policy and terms, linked from the landing footer and the Google consent screen. Open signed in or out. */
export function Legal({ page }: { page: "privacy" | "terms" }) {
  const sections = page === "privacy" ? privacy : terms;
  return (
    <main className="flex min-h-dvh flex-col bg-page px-5 pb-12 pt-6 md:px-10">
      <Link href="/" aria-label="Headcount home" className="self-start rounded-control">
        <Logo size={22} />
      </Link>
      <article className="mx-auto w-full max-w-[680px] py-12 md:py-16">
        <h1 className="font-display text-[40px] leading-[1.05] text-ink">{page === "privacy" ? "Privacy" : "Terms"}</h1>
        <p className="mt-3 text-sm text-muted">Updated {UPDATED}</p>
        {sections.map((s) => (
          <section key={s.title} className="mt-10">
            <h2 className="font-display text-[22px] text-ink">{s.title}</h2>
            <div className="mt-3 text-[15px] leading-relaxed text-ink-soft [&_a]:font-semibold [&_a]:text-primary-ink [&_a]:underline-offset-4 hover:[&_a]:underline [&_li]:mt-2 [&_ul]:list-disc [&_ul]:pl-5">
              {s.body}
            </div>
          </section>
        ))}
        <p className="mt-14 text-sm text-muted">
          <Link href={page === "privacy" ? "/terms" : "/privacy"} className="font-semibold text-primary-ink underline-offset-4 hover:underline">
            {page === "privacy" ? "Terms" : "Privacy"}
          </Link>
        </p>
      </article>
    </main>
  );
}
